/**
 * File: authorDirectoryUtils.ts
 * Path: apps/admin/src/shared/utils/authorDirectoryUtils.ts
 * Description: The author list behind the academy and portfolio editors.
 *
 * P2.6 tightened the `/users` rule so a signed-in editor can no longer read
 * every colleague's profile. The author dropdown still has to work for them,
 * so a refused directory read falls back to the one profile they may read —
 * their own — instead of leaving the picker empty or throwing.
 */
import type { UserProfile } from "@idk/types";

/** True for the Firestore rules rejection, not for a network or config error. */
export const isPermissionDenied = (error: unknown): boolean => {
  const code = String((error as { code?: unknown })?.code ?? "");
  if (code === "permission-denied" || code.endsWith("/permission-denied")) {
    return true;
  }
  const message = String((error as { message?: unknown })?.message ?? "");
  return /missing or insufficient permissions/i.test(message);
};

export interface AuthorDirectorySources {
  listAll: () => Promise<UserProfile[]>;
  loadSelf: (uid: string) => Promise<UserProfile | null>;
  uid?: string | null;
}

export const resolveAuthorDirectory = async ({
  listAll,
  loadSelf,
  uid,
}: AuthorDirectorySources): Promise<UserProfile[]> => {
  try {
    return await listAll();
  } catch (error) {
    // Anything other than the rules refusing is a real failure worth surfacing.
    if (!isPermissionDenied(error)) throw error;
    if (!uid) return [];

    const self = await loadSelf(uid);
    return self ? [self] : [];
  }
};
